import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andAllWhere,
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  noop,
  Normalizers,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "../../../../services/abstract.service";
import WarehouseAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/configurations/warehouse/warehouse.entity";
import { ICreateWarehouse } from "./interfaces/create-warehouse.interface";
import { IUpdateWarehouse } from "./interfaces/update-warehouse.interface";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { IFilters } from "../../filters/interfaces/filterInterface";

const apiUrl = env.API_CONFIGURATION_URL;
const moduleName = "warehouse";

@Injectable({
  providedIn: "root",
})
export class WarehouseService extends AbstractService<
  WarehouseAttributes,
  ICreateWarehouse,
  IUpdateWarehouse
> {

  /** The last filter used to load the list */
  currentFilter: CombinedFilter<WarehouseAttributes>;

  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  findByFilter(filter: CombinedFilter<WarehouseAttributes>, options?: RequestOptions) {
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    const headers: Record<string, any> = {};
    // see order-customer-export for more info about this header
    // ctx: stands for context
    if (options?.contextHeader) headers['x-ave-ctx'] = options.contextHeader;
    return this.http.get<IPaginationResult<WarehouseAttributes>>(API_URL, { headers });
  }

  /**
   * Obtiene almacenes con paginación y filtros.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Tres objetos (OR lógico) - busca en 'id', 'name', 'externalKeys.code'
   *
   * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
   *
   * @param options Pagination options
   * @param dataToFilter Filters to apply
   * @returns Paginated data
   *
   * Search fields: id, name, externalKeys.code
   * Array filters: statusId, plantId, companyId
   */
  findAllWithCount(
    options: IPagination<WarehouseAttributes>,
    dataToFilter: IFilters
  ): Observable<IPaginationResult<WarehouseAttributes>> {

    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, filtro de compañía, filtro de planta, filtro de estados.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<WarehouseAttributes>[] => {
      const deletedFilter: FilterFn<WarehouseAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        dataToFilter?.companyId
          ? andWhere('company.id' as keyof WarehouseAttributes, Ops.eq(dataToFilter.companyId))
          : noop(),
        dataToFilter?.plantId && dataToFilter.plantId.length > 0
          ? andWhere('plants.id' as keyof WarehouseAttributes, Ops.in(...dataToFilter.plantId))
          : noop(),
        dataToFilter?.status && dataToFilter.status.length > 0
          ? andWhere('statusId', Ops.in(...dataToFilter.status))
          : noop()
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por ID (o solo condiciones si no hay búsqueda)
    const searchByIdQueryObject: FilterFn<WarehouseAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("id", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
    const searchByNameQueryObject: FilterFn<WarehouseAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("name", Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 3: Condiciones base + búsqueda por CODE (solo si hay búsqueda)
    const searchByCodeQueryObject: FilterFn<WarehouseAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (tercer objeto)
      andWhere("externalKeys.code" as keyof WarehouseAttributes, Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<WarehouseAttributes>(
      // Expandimos los tres objetos en el buildQuery
      ...searchByIdQueryObject,
      ...searchByNameQueryObject,
      ...searchByCodeQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "name",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;

    return this.http.get<IPaginationResult<WarehouseAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(plantId?): Observable<IRequestResult<WarehouseAttributes[]>> {
    const query = buildQuery<WarehouseAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      plantId && plantId.length > 0 ? andAllWhere('plants._id' as keyof WarehouseAttributes, Ops.eq(plantId)) : noop(),
      select(["id", "externalKeys", "name", "plants"]),
      orderBy("id", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<WarehouseAttributes[]>>(API_URL);
  }

  /**
   * Filter to combo box
   * @returns Data
   */
  filterComboBox(filter): Observable<IRequestResult<WarehouseAttributes[]>> {
    const query = buildQuery<WarehouseAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      where('_deleted', false),
      andAllWhere('_id', Ops.in(...filter.officeWarehouse, Normalizers.ObjectId)),
      andAllWhere(<any>'company.id', Ops.in(...filter.company)),
      select(["id", "name", "plants", "externalKeys"]),
      orderBy("id", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<WarehouseAttributes[]>>(API_URL);
  }
}
